import log from 'loglevel';
import { fromJS, List, Map, Set } from 'immutable';

export type MessageType = string;

export type PendingMessageRule = {
    superior: MessageType;
    inferior: MessageType;
};

export type PendingMessage = {
    type: MessageType;
    data: any;
    customerId?: string;
    userId?: string;
};

export type PendingMessagesDirector = {
    rules: Map<MessageType, Set<MessageType>>;
    uniqueTypes: Set<MessageType>;
    messages: List<PendingMessage>;
};

export const newPendingMessagesDirector = (
    rules?: PendingMessageRule[],
    uniqueTypes?: MessageType[]
): PendingMessagesDirector => {
    const mappedRules = (rules || []).reduce((mapped, rule) => {
        return mapped.update(rule.superior, (inferiors) => {
            return inferiors ? inferiors.add(rule.inferior) : Set<MessageType>([rule.inferior]);
        });
    }, Map<MessageType, Set<MessageType>>());

    return {
        rules: mappedRules,
        uniqueTypes: Set(uniqueTypes || []),
        messages: List(),
    };
};

const filterMessagesByRules = (self: PendingMessagesDirector): List<PendingMessage> => {
    log.debug('pendingMessages: rules & messages', self.rules.toJS(), self.messages.toJS());

    const pendingSuperiors = self.messages.reduce((superiors, message) => {
        return superiors!.add(message!.type);
    }, Set<MessageType>());

    log.debug('pendingMessages: pendingSuperiors', pendingSuperiors.toJS());

    const inferiorsToOmit = pendingSuperiors.reduce((inferiors, superior) => {
        return inferiors!.merge(self.rules.get(superior!) || Set<MessageType>());
    }, Set<MessageType>());

    log.debug('pendingMessages: inferiorsToOmit', inferiorsToOmit.toJS());

    const messagesToEmit = self.messages
        .filter((message) => !inferiorsToOmit.has(message!.type))
        .toList();

    log.debug('pendingMessages: messagesToEmit', messagesToEmit.toJS());

    return messagesToEmit;
};

export const push = (self: PendingMessagesDirector, message: PendingMessage): void => {
    self.messages = self.messages
        .filter(
            (pendingMessage) =>
                !self.uniqueTypes.has(message.type) || pendingMessage?.type !== message.type
        )
        .toList()
        .push(message);
};

export const popAll = (self: PendingMessagesDirector): PendingMessage[] => {
    return filterMessagesByRules(self).toJS();
};
